import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useRef, useCallback } from 'react';
import { FiCopy, FiDollarSign, FiSettings } from 'react-icons/fi';
import { FaCheckCircle, FaRegArrowAltCircleRight } from 'react-icons/fa';
import { RxCrossCircled } from "react-icons/rx";
import { TbDetails, TbProgressHelp } from "react-icons/tb";
import moment from 'moment';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import { IoDiamond } from "react-icons/io5";

const DepositHistory = ({ theme = 'dark' }) => {
    const [filter, setFilter] = useState('all');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deposits, setDeposits] = useState([]);
    const [lastId, setLastId] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const observer = useRef();
    const navigate = useNavigate();

    const isDark = theme === 'dark';

    // Format amount with $ sign and proper formatting
    const formatAmount = (amount) => {
        if (!amount && amount !== 0) return '$0.00';
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `$${numAmount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const ReturnStatusIcon = (status) => {
        if (status === '0') {
            return (
                <span className='text-warning' title="Pending">
                    <TbProgressHelp />
                </span>
            );
        } else if (status === '1') {
            return (
                <span className='text-success' title="Approved">
                    <FaCheckCircle />
                </span>
            );
        } else if (status === '2') {
            return (
                <span className='text-danger' title="Rejected">
                    <RxCrossCircled />
                </span>
            );
        } else {
            return (
                <span className='text-danger' title="Unknown">
                    <TbProgressHelp />
                </span>
            );
        }
    };

    const getStatusBadge = (status) => {
        const config = {
            '0': {
                icon: TbProgressHelp,
                text: 'Pending',
                class: 'bg-warning text-dark'
            },
            '1': {
                icon: FaCheckCircle,
                text: 'Approved',
                class: 'bg-success text-white'
            },
            '2': {
                icon: RxCrossCircled,
                text: 'Rejected',
                class: 'bg-danger text-white'
            }
        };

        const statusConfig = config[status] || {
            icon: TbProgressHelp,
            text: 'Unknown',
            class: 'bg-secondary text-white'
        };
        const IconComponent = statusConfig.icon;

        return (
            <span className={`badge ${statusConfig.class} d-inline-flex align-items-center`}>
                <IconComponent className="me-1" />
                {statusConfig.text}
            </span>
        );
    };

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        toast.success('Copied!');
    };

    const openModal = (transaction) => {
        setSelectedTransaction(transaction);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedTransaction(null);
    };

    const fetchReport = async (selectedStatus = filter, loadMore = false) => {
        if (loading) return;

        setLoading(true);
        if (!loadMore) setInitialLoading(true);

        try {
            const formData = {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                last_id: loadMore ? lastId : 0,
                status: selectedStatus
            };

            const response = await axios.post(`${BASE_API_URL}/payment/deposit-report`, formData);
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                    return;
                } else {
                    toast.error(data?.error);
                    return;
                }
            }

            if (data?.data) {
                setHasMore(data?.has_more);
                setLastId(data?.last_id);

                if (loadMore) {
                    setDeposits(prevData => [...prevData, ...data.data]);
                } else {
                    setDeposits(data.data);
                }
            }
        } catch (error) {
            console.error('Fetch deposits error:', error);
            toast.error('Failed to fetch deposits');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const handleStatusChange = (status) => {
        setDeposits([]);
        setFilter(status);
        setLastId(0);
        setHasMore(true);
        fetchReport(status, false);
    };

    // Infinite scroll observer
    const lastElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                fetchReport(filter, true);
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, hasMore, filter]);

    useEffect(() => {
        fetchReport('all', false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5
            }
        }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.3
            }
        }
    };

    const mutedTextClass = isDark ? 'text-light-emphasis' : 'text-muted';
    const headerBgClass = isDark ? 'bg-dark' : 'bg-light';
    const sectionBgClass = isDark ? 'bg-dark' : 'bg-white';
    const headerTextClass = isDark ? 'text-light' : 'text-dark';

    return (
        <>
            <style>{`
                .deposit-history-container {
                    min-height: 100vh;
                    background: var(--dh-bg);
                    color: var(--dh-text-main);
                }

                .table-responsive {
                    margin: 0;
                    padding: 0;
                }

                .deposit-table {
                    font-size: 0.8rem;
                }

                .table-header {
                    background-color: var(--dh-header-bg) !important;
                }

                .table-header th {
                    border: none;
                    font-weight: 700;
                    color: var(--dh-text-main);
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    text-align: left;
                    border-bottom: 2px solid var(--dh-header-border);
                }

                .table-row:hover {
                    background-color: var(--dh-row-hover);
                }

                .table-content {
                    font-weight: 400;
                    color: var(--dh-text-main);
                }

                .amount {
                    color: var(--dh-amount);
                    font-weight: 600;
                }

                .method {
                    text-transform: uppercase;
                }

                .status-icon {
                    font-size: 1.1rem;
                }

                .view-btn {
                    background: none;
                    border: none;
                    color: #0d6efd;
                    font-size: 1.1rem;
                    cursor: pointer;
                }

                .view-btn:hover {
                    color: #0b5ed7;
                }

                .loading-spinner {
                    display: inline-block;
                    width: 20px;
                    height: 20px;
                    border: 2px solid #4b5563;
                    border-radius: 50%;
                    border-top-color: #0d6efd;
                    animation: spin 1s ease-in-out infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .modal-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                    padding: 15px;
                }

                .modal-content {
                    background: var(--dh-modal-bg);
                    color: var(--dh-text-main);
                    border-radius: 10px;
                    width: 100%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
                    padding: 20px;
                    border: 1px solid var(--dh-modal-border);
                }

                .modal-body {
                    padding: 20px;
                    overflow-y: auto;
                }

                /* Hide scrollbar for modal */
                .modal-body::-webkit-scrollbar {
                    display: none;
                }

                .modal-body {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }

                .amount-summary {
                    background: var(--dh-summary-bg);
                    border-radius: 6px;
                    border: 1px solid var(--dh-summary-border);
                }

                .amount-number {
                    font-weight: 600;
                    font-size: 1.1rem;
                }

                .total-amount {
                    color: #28a745;
                }

                /* Mobile responsive */
                @media (max-width: 768px) {
                    .deposit-table {
                        font-size: 0.7rem;
                    }

                    .modal-content {
                        margin: 0;
                        width: 100%;
                        max-width: 100%;
                        border-radius: 0;
                        max-height: 100vh;
                    }

                    .modal-body {
                        padding: 15px;
                    }

                    .amount-number {
                        font-size: 1rem;
                    }
                }

                @media (max-width: 576px) {
                    .deposit-table {
                        font-size: 0.65rem;
                    }
                }
            `}</style>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="container-fluid deposit-history-container p-0"
                style={{
                    '--dh-bg': isDark ? '#020617' : '#ffffff',
                    '--dh-header-bg': isDark ? '#020617' : '#f8f9fa',
                    '--dh-header-border': isDark ? '#1f2937' : '#dee2e6',
                    '--dh-row-hover': isDark ? '#111827' : '#f8f9fa',
                    '--dh-text-main': isDark ? '#e5e7eb' : '#111827',
                    '--dh-amount': isDark ? '#93c5fd' : '#2c5aa0',
                    '--dh-modal-bg': isDark ? '#020617' : '#ffffff',
                    '--dh-modal-border': isDark ? '#1f2937' : '#dee2e6',
                    '--dh-summary-bg': isDark ? '#020617' : '#f8f9fa',
                    '--dh-summary-border': isDark ? '#1f2937' : '#dee2e6',
                }}
            >
                {/* Header Section */}
                <div className={`${headerBgClass} p-3 border-bottom`}>
                    <div className="d-flex align-items-center">
                        <div className="me-3">
                            <IoDiamond
                                className="text-success rounded-circle p-2"
                                style={{
                                    fontSize: '2rem',
                                    backgroundColor: isDark ? '#064e3b' : '#e8f5e8'
                                }}
                            />
                        </div>
                        <div>
                            <h1 className={`h5 fw-bold mb-0 ${headerTextClass}`}>Deposit History</h1>
                        </div>
                    </div>
                </div>

                {/* Filter Section */}
                <div className={`${sectionBgClass} p-3 border-bottom`}>
                    <div className="row align-items-center g-2">
                        <div className="col-auto">
                            <label className={`form-label fw-medium mb-0 small ${mutedTextClass}`}>
                                Status:
                            </label>
                        </div>
                        <div className="col">
                            <select
                                value={filter}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                className="form-select form-select-sm"
                            >
                                <option value="all">All</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Deposit Table */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {initialLoading ? (
                        <div className="text-center py-4">
                            <div className="loading-spinner mb-2"></div>
                            <p className={`${mutedTextClass} mb-0 small`}>Loading...</p>
                        </div>
                    ) : deposits.length === 0 ? (
                        <div className="text-center py-5">
                            <FiDollarSign
                                className={`${mutedTextClass} mb-3`}
                                style={{ fontSize: '3rem', opacity: 0.5 }}
                            />
                            <p className={`${mutedTextClass} mb-0`}>No transactions found</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover deposit-table mb-0">
                                <thead>
                                    <tr className="table-header">
                                        <th className="ps-3 align-middle">#</th>
                                        <th className="align-middle">Date & Time</th>
                                        <th className="align-middle">Method</th>
                                        <th className="align-middle">Amount</th>
                                        <th className="text-center align-middle">Status</th>
                                        <th className="text-center align-middle">
                                            <FiSettings />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deposits.map((item, index) => (
                                        <motion.tr
                                            key={item.id || item.deposit_id || index}
                                            variants={itemVariants}
                                            className="table-row"
                                            ref={index === deposits.length - 1 ? lastElementRef : null}
                                        >
                                            <td className="ps-3 align-middle table-content">{index + 1}</td>
                                            <td className="align-middle table-content">
                                                <div className="d-flex flex-column">
                                                    <span>
                                                        {moment(item.date, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YY")}
                                                    </span>
                                                    <small className={mutedTextClass}>
                                                        {moment(item.date, "YYYY-MM-DD HH:mm:ss").format("h:mm A")}
                                                    </small>
                                                </div>
                                            </td>
                                            <td className="method align-middle table-content">{item?.method}</td>
                                            <td className="amount align-middle table-content">
                                                {formatAmount(item?.amount)}
                                            </td>
                                            <td className="text-center align-middle">
                                                <div className="status-icon">
                                                    {ReturnStatusIcon(item.status)}
                                                </div>
                                            </td>
                                            <td className="text-center align-middle">
                                                <button
                                                    className="view-btn"
                                                    onClick={() => openModal(item)}
                                                    title="View Details"
                                                >
                                                    <FaRegArrowAltCircleRight />
                                                </button>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Loading indicator for infinite scroll */}
                            {loading && hasMore && (
                                <div className="text-center py-3">
                                    <div className="loading-spinner"></div>
                                </div>
                            )}

                            {/* End of results */}
                            {!hasMore && deposits.length > 0 && (
                                <div className="text-center py-3">
                                    <p className={`${mutedTextClass} mb-0 small`}>No more transactions</p>
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>

                {/* Transaction Details Modal */}
                <AnimatePresence>
                    {showModal && selectedTransaction && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="modal-backdrop"
                            onClick={closeModal}
                        >
                            <motion.div
                                variants={modalVariants}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                                className="modal-content"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className={`modal-header border-0 ${headerBgClass}`}>
                                    <h5 className={`modal-title fw-bold ${headerTextClass}`}>
                                        Transaction Details
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeModal}
                                    ></button>
                                </div>

                                <div className="modal-body">
                                    {/* Transaction ID and Method */}
                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                        <span className={`small fw-medium ${mutedTextClass}`}>
                                            Transaction ID
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="fw-medium">
                                                {selectedTransaction?.id}
                                            </span>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => copyToClipboard(selectedTransaction?.id)}
                                            >
                                                <FiCopy className="small" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                        <span className={`small fw-medium ${mutedTextClass}`}>
                                            Payment Method
                                        </span>
                                        <span className="fw-medium text-uppercase">
                                            {selectedTransaction?.method}
                                        </span>
                                    </div>

                                    {/* Date and Status */}
                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                        <span className={`small fw-medium ${mutedTextClass}`}>
                                            Date & Time
                                        </span>
                                        <span className="fw-medium">
                                            {moment(selectedTransaction?.date, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY h:mm A")}
                                        </span>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                        <span className={`small fw-medium ${mutedTextClass}`}>
                                            Status
                                        </span>
                                        <div>
                                            {getStatusBadge(selectedTransaction?.status)}
                                        </div>
                                    </div>

                                    {/* Reference Number */}
                                    <div className="mb-3">
                                        <label className={`form-label small fw-medium ${mutedTextClass}`}>
                                            Reference Number
                                        </label>
                                        <div className="input-group input-group-sm">
                                            <textarea
                                                className="form-control"
                                                rows={1}
                                                readOnly
                                                value={selectedTransaction?.ref_no}
                                                style={{ fontSize: '0.8rem', resize: 'none' }}
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => copyToClipboard(selectedTransaction?.ref_no)}
                                            >
                                                <FiCopy className="small" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Amount Summary */}
                                    <div className="amount-summary p-3 mb-3">
                                        <div className="row text-center">
                                            <div className="col-4">
                                                <div className={`small fw-medium ${mutedTextClass}`}>
                                                    Amount
                                                </div>
                                                <div className="amount-number">
                                                    {formatAmount(selectedTransaction?.amount)}
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className={`small fw-medium ${mutedTextClass}`}>
                                                    Bonus
                                                </div>
                                                <div className="amount-number">
                                                    +{formatAmount(selectedTransaction?.bonus || 0)}
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className={`small fw-medium ${mutedTextClass}`}>
                                                    Total
                                                </div>
                                                <div className="amount-number total-amount">
                                                    {formatAmount(selectedTransaction?.total || selectedTransaction?.amount)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Rejection Reason */}
                                    {selectedTransaction?.status === '2' && selectedTransaction?.reject_reason && (
                                        <div className="alert alert-danger small mb-3">
                                            <div className="fw-medium mb-1">Rejection Reason</div>
                                            <p className="mb-0">{selectedTransaction.reject_reason}</p>
                                        </div>
                                    )}

                                    {/* Remarks */}
                                    {selectedTransaction?.remark && (
                                        <div className="alert alert-info small mb-0">
                                            <div className="fw-medium mb-1">Remarks</div>
                                            <p className="mb-0">{selectedTransaction.remark}</p>
                                        </div>
                                    )}
                                </div>

                                <div className={`modal-footer border-0 ${headerBgClass}`}>
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        onClick={closeModal}
                                    >
                                        Close
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </>
    );
};

export default DepositHistory;
